import { RichTextEditor } from '@mantine/tiptap';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import Table from '@tiptap/extension-table';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import TableRow from '@tiptap/extension-table-row';
import { useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import React, { useEffect, useState } from 'react';
import { Markdown } from 'tiptap-markdown';
import {
    MarkdownEditorAddTableButton,
    MarkdownEditorAddRowBefore,
    MarkdownEditorRemoveTableButton,
    MarkdownEditorAddRowAfter,
    MarkdownEditorAddColumnBefore,
    MarkdownEditorAddColumnAfter,
    MarkdownEditorRemoveColumn,
    MarkdownEditorRemoveRow,
} from './markdownEditorControls';

type MarkdownEditorProps = {
    value: string;
    placeholder?: string | undefined;
    size?: 'small' | 'medium' | 'large' | undefined;
    hasTableEditor?: undefined | boolean;
    onChange(value: string): void;
};

export function MarkdownEditor(props: MarkdownEditorProps) {
    const { value } = props;

    const [currentValue, setCurrentValue] = useState(value);

    let sizeClass;
    switch (props.size) {
        case 'small':
            sizeClass = 'ProseMirror-sm';
            break;
        case 'medium':
            sizeClass = 'ProseMirror-md';
            break;
        case 'large':
            sizeClass = 'ProseMirror-lg';
            break;
        default:
            sizeClass = 'ProseMirror-md';
            break;
    }

    const editor = useEditor({
        editorProps: {
            attributes: {
                class: sizeClass,
            },
        },
        extensions: [
            StarterKit,
            Markdown.configure({
                html: false,
            }),
            Table,
            TableRow,
            TableHeader,
            TableCell,
            Link.configure({ openOnClick: false }),
            Placeholder.configure({
                emptyEditorClass: 'is-editor-empty',
                placeholder: props.placeholder ?? '',
            }),
        ],
        content: currentValue,

        onBlur: (e) => {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
            props.onChange(e.editor.storage.markdown.serializer.serialize(e.editor.state.doc));
        },
    });

    useEffect(() => {
        setCurrentValue(value);
        if (editor) {
            editor.commands.setContent(value);
        }
    }, [editor, value]);

    return (
        <RichTextEditor editor={editor} className="markdown-table">
            <RichTextEditor.Toolbar sticky stickyOffset={60}>
                <RichTextEditor.ControlsGroup>
                    <RichTextEditor.Bold />
                    <RichTextEditor.Italic />
                    <RichTextEditor.Strikethrough />
                    <RichTextEditor.ClearFormatting />
                </RichTextEditor.ControlsGroup>

                <RichTextEditor.ControlsGroup>
                    <RichTextEditor.H1 />
                    <RichTextEditor.H2 />
                    <RichTextEditor.H3 />
                    <RichTextEditor.H4 />
                </RichTextEditor.ControlsGroup>

                <RichTextEditor.ControlsGroup>
                    <RichTextEditor.Blockquote />
                    <RichTextEditor.Hr />
                    <RichTextEditor.BulletList />
                    <RichTextEditor.OrderedList />
                </RichTextEditor.ControlsGroup>

                <RichTextEditor.ControlsGroup>
                    <RichTextEditor.Link />
                    <RichTextEditor.Unlink />
                </RichTextEditor.ControlsGroup>

                {props.hasTableEditor && (
                    <RichTextEditor.ControlsGroup>
                        <MarkdownEditorAddTableButton />
                        <MarkdownEditorRemoveTableButton />
                        <MarkdownEditorAddColumnBefore />
                        <MarkdownEditorRemoveColumn />
                        <MarkdownEditorAddColumnAfter />
                        <MarkdownEditorAddRowBefore />
                        <MarkdownEditorRemoveRow />
                        <MarkdownEditorAddRowAfter />
                    </RichTextEditor.ControlsGroup>
                )}

                <RichTextEditor.ControlsGroup>
                    <RichTextEditor.Undo />
                    <RichTextEditor.Redo />
                </RichTextEditor.ControlsGroup>
            </RichTextEditor.Toolbar>

            <RichTextEditor.Content />
        </RichTextEditor>
    );
}
